import { ExternalLinkIcon, FileQuestionIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { EmptyState } from "@/components/shared/empty";

import { resolveMediaUrl } from "@/lib/media";

import { detectViewerKind } from "./detect-kind";

export { detectViewerKind } from "./detect-kind";
export type { ViewerKind } from "./detect-kind";

interface FileViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Faylning (odatda presigned) manzili. HECH QAYERDA matn sifatida chiqmaydi. */
  url?: string | null;
  /** Ko'ruvchining sarlavhasi — fayl nomi emas, uning MA'NOSI ("Diplom"). */
  title: string;
  description?: string;
}

/**
 * Fayl ko'ruvchi: diplom, rezyume va ilovalar shu dialog ichida ochiladi,
 * sarlavhadagi tugma esa faylni YANGI OYNADA ochadi (brauzerning o'z PDF/video
 * ko'rgichi, yuklab olish — foydalanuvchi talabi, 2026-10-04). Ichki ko'ruvchi
 * ko'rsatolmaydigan tur (docx, zip …) uchun ham shu tugma ishlaydi.
 */
export const FileViewer = ({
  open,
  onOpenChange,
  url: rawUrl,
  title,
  description,
}: FileViewerProps) => {
  const { t } = useTranslation();
  const kind = detectViewerKind(rawUrl);
  // Proksi havolasi nisbiy keladi va panelning o'z manziliga ketardi.
  const url = resolveMediaUrl(rawUrl);

  const body = () => {
    if (!url) {
      return (
        <EmptyState
          icon={FileQuestionIcon}
          title={t("VIEWER.MISSING")}
          description={t("VIEWER.MISSING_HINT")}
        />
      );
    }

    switch (kind) {
      case "image":
        return (
          <img
            src={url}
            alt={title}
            className="bg-muted max-h-[70vh] w-full rounded-xl object-contain"
          />
        );
      case "pdf":
        return (
          <iframe
            src={url}
            title={title}
            className="bg-muted h-[70vh] w-full rounded-xl border-0"
          />
        );
      case "video":
        return (
          <video src={url} controls className="bg-muted max-h-[70vh] w-full rounded-xl">
            <track kind="captions" />
          </video>
        );
      case "audio":
        return <audio src={url} controls className="w-full" />;
      default:
        return (
          <EmptyState
            icon={FileQuestionIcon}
            title={t("VIEWER.UNSUPPORTED")}
            description={t("VIEWER.UNSUPPORTED_HINT")}
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl sm:max-w-4xl">
        <DialogHeader className="pr-8">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <DialogTitle>{title}</DialogTitle>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
              >
                <ExternalLinkIcon className="size-4" aria-hidden />
                {t("VIEWER.OPEN_NEW_TAB")}
              </a>
            )}
          </div>
          <DialogDescription>{description ?? t("VIEWER.DESCRIPTION")}</DialogDescription>
        </DialogHeader>

        {open && body()}
      </DialogContent>
    </Dialog>
  );
};
